import React from 'react';

/**
 * Consolidated "advanced" offer knobs for the campaign builder, so every
 * offer-related field lives together in one campaign. Backed by a single
 * `advanced` object + `onChange(patch)` to avoid prop-drilling 14 setters.
 *
 * Knobs: 1 Mechanic (tiered / flat-price / free-item), 2 Trigger (lead time),
 * 3 Eligibility (membership / referral), 4 Timing (happy-hour windows),
 * 5 Guardrails (budget ceiling / floor), 6 Stacking, 7 Redemption mode,
 * 8 Target retention bucket (bucket-gated delivery).
 */

const RETENTION_BUCKET_OPTIONS = [
  { value: '', label: 'Everyone — no bucket targeting' },
  { value: 'DORMANT', label: 'Dormant — lapsed customers (win-back)' },
  { value: 'SLIPPING_AWAY', label: 'Slipping away — well past their cycle' },
  { value: 'AT_RISK', label: 'At risk — overdue for a return visit' },
  { value: 'DUE_SOON', label: 'Due soon — approaching their return date' },
  { value: 'LOYAL_ON_TIME', label: 'Loyal / on-time — regulars (recognition)' },
  { value: 'HIGH_SPEND', label: 'High spend — premium customers' },
  { value: 'OFFER_SENSITIVE', label: 'Offer sensitive — coupon-driven customers' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const labelStyle = { fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted, #94a3b8)' };
const groupStyle = { display: 'grid', gap: 8, marginTop: 18 };
const rowStyle = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' };
const hintStyle = { fontSize: 12, color: 'var(--text-muted, #94a3b8)' };

function NumberField({ label, value, onChange, placeholder, prefix, suffix, min = 0 }) {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span style={hintStyle}>{label}</span>
      <span style={rowStyle}>
        {prefix ? <span style={hintStyle}>{prefix}</span> : null}
        <input
          type="number"
          min={min}
          className="analytics-select"
          style={{ width: 120 }}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
        {suffix ? <span style={hintStyle}>{suffix}</span> : null}
      </span>
    </label>
  );
}

export default function AdvancedCampaignRules({ advanced, onChange, section }) {
  const a = advanced || {};
  const patch = (next) => onChange?.(next);
  const showWhen = !section || section === 'when';
  const showLimits = !section || section === 'limits';

  const windows = Array.isArray(a.timeWindows) ? a.timeWindows : [];

  const setWindow = (idx, key, val) => {
    const next = windows.map((w, i) => (i === idx ? { ...w, [key]: val } : w));
    patch({ timeWindows: next });
  };
  const toggleWindowDay = (idx, day) => {
    const w = windows[idx] || {};
    const days = Array.isArray(w.days) ? w.days : [];
    const nextDays = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
    setWindow(idx, 'days', nextDays);
  };
  const addWindow = () => patch({ timeWindows: [...windows, { days: [], start: '', end: '' }] });
  const removeWindow = (idx) => patch({ timeWindows: windows.filter((_, i) => i !== idx) });

  return (
    <div className="offers-advanced-rules">
      {showWhen && (
      <>
      {/* Knob 4 — Timing: happy-hour / off-peak windows */}
      <div style={groupStyle}>
        <label style={labelStyle}>Happy hours / off-peak windows</label>
        <span style={hintStyle}>Offer applies only when the appointment falls in one of these recurring windows.</span>
        {windows.map((w, idx) => (
          <div key={idx} style={{ display: 'grid', gap: 6, padding: 10, border: '1px solid rgba(148,163,184,0.14)', borderRadius: 10 }}>
            <div style={rowStyle}>
              {DAYS.map((day) => {
                const on = Array.isArray(w.days) && w.days.includes(day);
                return (
                  <button key={day} type="button"
                    className={`catalog-action ${on ? 'catalog-action--success' : ''}`}
                    onClick={() => toggleWindowDay(idx, day)}>{day}</button>
                );
              })}
            </div>
            <div style={rowStyle}>
              <span style={hintStyle}>From</span>
              <input type="time" className="analytics-select" value={w.start || ''} onChange={(e) => setWindow(idx, 'start', e.target.value)} />
              <span style={hintStyle}>to</span>
              <input type="time" className="analytics-select" value={w.end || ''} onChange={(e) => setWindow(idx, 'end', e.target.value)} />
              <button type="button" className="catalog-action catalog-action--danger" onClick={() => removeWindow(idx)}>Remove</button>
            </div>
          </div>
        ))}
        <button type="button" className="catalog-action" onClick={addWindow}>+ Add window</button>
      </div>

      {/* Knob 2 — Booking-time triggers */}
      <div style={groupStyle}>
        <label style={labelStyle}>Booking-time triggers</label>
        <div style={rowStyle}>
          <NumberField label="Last-minute: book within (hours before)" suffix="h" value={a.leadTimeMaxHours}
            onChange={(v) => patch({ leadTimeMaxHours: v })} placeholder="4" />
          <NumberField label="Early-bird: book at least (hours before)" suffix="h" value={a.leadTimeMinHours}
            onChange={(v) => patch({ leadTimeMinHours: v })} placeholder="48" />
        </div>
      </div>

      {/* Knob 3 — Eligibility (advanced) */}
      <div style={groupStyle}>
        <label style={labelStyle}>Eligibility (advanced)</label>
        <div style={rowStyle}>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={hintStyle}>Members of tier (optional)</span>
            <input className="analytics-select" style={{ width: 200 }} value={a.membershipTier || ''} placeholder="e.g. Gold"
              onChange={(e) => patch({ membershipTier: e.target.value })} />
          </label>
          <label style={{ ...rowStyle, gap: 6 }}>
            <input type="checkbox" checked={!!a.requiresReferral} onChange={(e) => patch({ requiresReferral: e.target.checked })} />
            <span style={hintStyle}>Requires referral</span>
          </label>
        </div>
      </div>
      </>
      )}

      {showLimits && (
      <>
      {/* Knob 5 — Guardrails */}
      <div style={groupStyle}>
        <label style={labelStyle}>Budget and caps</label>
        <span style={hintStyle}>Stop offers from giving away more discount than intended.</span>
        <div style={rowStyle}>
          <NumberField
            label="Total campaign budget"
            prefix="Rs"
            value={a.budgetCeiling}
            onChange={(v) => patch({ budgetCeiling: v })}
            placeholder="5000"
            min={1}
          />
          <NumberField
            label="Minimum discount floor"
            prefix="Rs"
            value={a.minDiscountFloor}
            onChange={(v) => patch({ minDiscountFloor: v })}
            placeholder="50"
            min={1}
          />
        </div>
      </div>

      {/* Knob 6 — Stacking */}
      <div style={groupStyle}>
        <label style={labelStyle}>Stacking</label>
        <div style={rowStyle}>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={hintStyle}>Combination rule</span>
            <select className="analytics-select" value={a.stackingMode || 'EXCLUSIVE'} onChange={(e) => patch({ stackingMode: e.target.value })}>
              <option value="EXCLUSIVE">Exclusive — do not combine with other offers</option>
              <option value="STACKABLE">Stackable — can combine when eligible</option>
            </select>
          </label>
          <NumberField
            label="Priority"
            value={a.stackPriority}
            onChange={(v) => patch({ stackPriority: v })}
            placeholder="0"
            min={0}
          />
        </div>
      </div>

      {/* Knob 7 — Redemption */}
      <div style={groupStyle}>
        <label style={labelStyle}>Redemption</label>
        <select className="analytics-select" value={a.redemptionMode || 'CODE'} onChange={(e) => patch({ redemptionMode: e.target.value })}>
          <option value="CODE">Coupon code — customer enters a code</option>
          <option value="AUTO">Automatic — applies at booking when conditions match</option>
        </select>
      </div>

      {/* Knob 8 — Bucket-targeted delivery */}
      <div style={groupStyle}>
        <label style={labelStyle}>Target bucket</label>
        <span style={hintStyle}>Only customers currently in this retention bucket can see and claim the offer.</span>
        <select className="analytics-select" value={a.targetRetentionBucket || ''} onChange={(e) => patch({ targetRetentionBucket: e.target.value })}>
          {RETENTION_BUCKET_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      </>
      )}
    </div>
  );
}
